import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AlertDefinition,
  DefinitionListOptions,
} from "@shared-core/data/adapter";
import { formatCount } from "@shared-core/domain/formatters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Badge } from "../app/components/primitives/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Plus, Search, Edit, Trash2 } from "lucide-react";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { isFeatureEnabled } from "@shared-core/config/features";
import { paths } from "@shared-core/routing/routes";

export function SettingsAlertsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState<AlertDefinition[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retireId, setRetireId] = useState<string | null>(null);
  // See SettingsSignalsPage — viewing definitions is production, authoring them
  // is not. This hides the controls; the definition actions router carrying
  // `require_feature("recommendations_config_edit")` is what closes the writes.
  const canEditDefinitions = isFeatureEnabled("recommendations_config_edit");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [familyFilter, setFamilyFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [families, setFamilies] = useState<Array<{ id: string; name: string }>>(
    [],
  );

  useEffect(() => {
    loadFamilies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on the listed filters; load fn is recreated every render
  }, []);

  useEffect(() => {
    loadDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on the listed filters; load fn is recreated every render
  }, [search, statusFilter, familyFilter, page]);

  const loadFamilies = async () => {
    const data = await dataAdapter.getAlertFamilyOptions();
    setFamilies(data);
  };

  const loadDefinitions = async () => {
    setLoading(true);
    const options: DefinitionListOptions = {
      search: search || undefined,
      status: statusFilter !== "all" ? (statusFilter as any) : undefined,
      family: familyFilter !== "all" ? familyFilter : undefined,
      page,
      pageSize,
    };
    const response = await dataAdapter.getAlertDefinitions(options);
    setDefinitions(response.items);
    setTotal(response.total);
    setLoading(false);
  };

  const handleRetire = async () => {
    if (!retireId) return;
    await dataAdapter.updateAlertDefinition(retireId, { status: "inactive" });
    setRetireId(null);
    loadDefinitions();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      live: "default",
      inactive: "secondary",
      draft: "outline",
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Alert Definitions</h1>
          <p className="text-muted-foreground">
            Configure alert expressions to notify teams of important customer
            conditions
          </p>
        </div>
        {canEditDefinitions && (
          <Button onClick={() => navigate(paths.settingsAlertsCreate())}>
            <Plus className="size-4 mr-2" />
            Create Alert
          </Button>
        )}
      </div>

      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-48">
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <label className="text-sm font-medium mb-2 block">Family</label>
          <Select
            value={familyFilter}
            onValueChange={(v) => {
              setFamilyFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Families</SelectItem>
              {families.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="max-w-[400px]">Description</TableHead>
              <TableHead>Family</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : definitions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No alert definitions found
                </TableCell>
              </TableRow>
            ) : (
              definitions.map((def) => (
                <TableRow key={def.id}>
                  <TableCell className="font-medium">{def.name}</TableCell>
                  <TableCell
                    className="text-sm text-muted-foreground max-w-[400px]"
                    title={def.description || undefined}
                  >
                    <span className="line-clamp-2">
                      {def.description || "-"}
                    </span>
                  </TableCell>
                  <TableCell className="capitalize">{def.family}</TableCell>
                  <TableCell>{getStatusBadge(def.status)}</TableCell>
                  <TableCell>v{def.version}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateOrDash(def.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {canEditDefinitions && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(paths.settingsAlertEdit({ id: def.id }))
                            }
                          >
                            <Edit className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRetireId(def.id)}
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {formatCount((page - 1) * pageSize + 1)} to{" "}
            {formatCount(Math.min(page * pageSize, total))} of{" "}
            {formatCount(total)} results
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!retireId} onOpenChange={() => setRetireId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire Alert Definition?</AlertDialogTitle>
            <AlertDialogDescription>
              This sets the definition to inactive. It stops evaluating but
              version history is preserved and it can be reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetire}>Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
